/**
 * PlatformPricing — global pricing management page (platform admin only).
 * Lets you set RRP & Reseller monthly price per module, and create/manage
 * optional named discounts (percent or fixed).
 *
 * Per-customer agreed prices live on the company page (Modules tab) and take
 * priority over RRP when invoicing.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import {
  Save, RefreshCw, Plus, Trash2, RotateCcw, AlertTriangle, Tag, Sparkles, ArrowLeft,
} from 'lucide-react';
import { formatPrice } from '../modules/catalog';

const APPLIES_TO_OPTIONS = [
  { value: 'any', label: 'Any customer' },
  { value: 'direct', label: 'Direct customers only' },
  { value: 'reseller', label: 'Reseller customers only' },
];

const EMPTY_DISCOUNT = {
  code: '',
  name: '',
  discountType: 'percent',
  amount: '',
  appliesTo: 'any',
  isActive: true,
  expiresAt: '',
  notes: '',
};

export default function PlatformPricing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [modules, setModules] = useState([]);
  // edits: { code -> { rrpMonthly: string, resellerMonthly: string } }
  const [edits, setEdits] = useState({});
  const [discounts, setDiscounts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newDiscount, setNewDiscount] = useState({ ...EMPTY_DISCOUNT });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/platform/pricing');
      const mods = res.data.modules || [];
      setModules(mods);
      const e = {};
      mods.forEach(m => {
        e[m.code] = {
          rrpMonthly: m.rrpMonthly != null ? String(m.rrpMonthly) : '',
          resellerMonthly: m.resellerMonthly != null ? String(m.resellerMonthly) : '',
        };
      });
      setEdits(e);
      setDiscounts(res.data.discounts || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateEdit = (code, field, raw) => {
    if (raw !== '' && !/^\d*(\.\d{0,2})?$/.test(raw)) return;
    setEdits(prev => ({ ...prev, [code]: { ...prev[code], [field]: raw } }));
  };

  const isModuleDirty = (code) => {
    const m = modules.find(x => x.code === code);
    if (!m) return false;
    const e = edits[code] || { rrpMonthly: '', resellerMonthly: '' };
    const a = m.rrpMonthly != null ? String(m.rrpMonthly) : '';
    const b = m.resellerMonthly != null ? String(m.resellerMonthly) : '';
    return e.rrpMonthly !== a || e.resellerMonthly !== b;
  };

  const saveModule = async (code) => {
    setSaving(true);
    try {
      const e = edits[code];
      const payload = {
        rrpMonthly: e.rrpMonthly === '' ? null : Number(e.rrpMonthly),
        resellerMonthly: e.resellerMonthly === '' ? null : Number(e.resellerMonthly),
      };
      const res = await api.put(`/platform/pricing/modules/${code}`, payload);
      const mods = res.data.modules || [];
      setModules(mods);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetModuleToDefault = (code) => {
    const m = modules.find(x => x.code === code);
    setEdits(prev => ({
      ...prev,
      [code]: {
        rrpMonthly: m?.defaultRRP != null ? String(m.defaultRRP) : '',
        resellerMonthly: '',
      },
    }));
  };

  const createDiscount = async () => {
    setSaving(true);
    try {
      const payload = {
        ...newDiscount,
        amount: Number(newDiscount.amount),
        expiresAt: newDiscount.expiresAt || null,
      };
      const res = await api.post('/platform/pricing/discounts', payload);
      setDiscounts(res.data.discounts || []);
      setShowNew(false);
      setNewDiscount({ ...EMPTY_DISCOUNT });
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to create discount');
    } finally {
      setSaving(false);
    }
  };

  const updateDiscountField = (id, patch) => {
    setDiscounts(prev => prev.map(d => d.discountId === id ? { ...d, ...patch, _dirty: true } : d));
  };

  const saveDiscount = async (d) => {
    setSaving(true);
    try {
      const payload = {
        code: d.code,
        name: d.name,
        discountType: d.discountType,
        amount: Number(d.amount),
        appliesTo: d.appliesTo,
        isActive: !!d.isActive,
        expiresAt: d.expiresAt || null,
        notes: d.notes || null,
      };
      const res = await api.put(`/platform/pricing/discounts/${d.discountId}`, payload);
      setDiscounts(res.data.discounts || []);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save discount');
    } finally {
      setSaving(false);
    }
  };

  const deleteDiscount = async (d) => {
    if (!window.confirm(`Delete discount "${d.name}" (${d.code})? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const res = await api.delete(`/platform/pricing/discounts/${d.discountId}`);
      setDiscounts(res.data.discounts || []);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to delete discount');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex items-center gap-3 text-gray-500">
        <RefreshCw className="w-5 h-5 animate-spin" /> Loading pricing…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <div className="font-semibold flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Failed to load pricing</div>
          <p className="text-sm mt-1">{error}</p>
          <button onClick={load} className="mt-3 px-3 py-1.5 text-sm bg-red-600 text-white rounded">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-800 inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-blue-600" /> Module Pricing &amp; Discounts
        </h1>
        <p className="text-gray-600 mt-1">
          Manage the recommended retail price (RRP) and reseller price for each module,
          plus optional discount codes. Per-customer agreed prices set on each company's
          <span className="font-medium"> Modules </span>
          tab override these defaults when invoicing.
        </p>
      </div>

      {/* MODULE PRICING */}
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <header className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Module pricing</h2>
            <p className="text-xs text-gray-500 mt-0.5">All prices are in GBP (£) per month, per company.</p>
          </div>
        </header>
        <div>
          <table className="w-full text-sm divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Default RRP</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">RRP / month</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reseller / month</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margin</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {modules.map(m => {
                const e = edits[m.code] || { rrpMonthly: '', resellerMonthly: '' };
                const dirty = isModuleDirty(m.code);
                const rrpN = Number(e.rrpMonthly);
                const reselN = Number(e.resellerMonthly);
                const margin = (Number.isFinite(rrpN) && Number.isFinite(reselN) && rrpN > 0)
                  ? Math.round(((rrpN - reselN) / rrpN) * 1000) / 10
                  : null;
                return (
                  <tr key={m.code} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {m.name}
                        {m.alwaysOn && <span className="px-1.5 py-0.5 text-[10px] bg-gray-200 text-gray-700 rounded">CORE</span>}
                        {m.comingSoon && <span className="px-1.5 py-0.5 text-[10px] bg-purple-100 text-purple-700 rounded">SOON</span>}
                      </div>
                      <div className="text-xs text-gray-500">{m.description}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">{formatPrice(m.defaultRRP)}</td>
                    <td className="px-4 py-3 text-right">
                      <PriceInput value={e.rrpMonthly} onChange={(v) => updateEdit(m.code, 'rrpMonthly', v)} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <PriceInput value={e.resellerMonthly} onChange={(v) => updateEdit(m.code, 'resellerMonthly', v)} />
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-gray-600">
                      {margin != null ? `${margin}%` : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => resetModuleToDefault(m.code)}
                        title="Reset RRP to catalog default and clear reseller"
                        className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded mr-1"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => saveModule(m.code)}
                        disabled={!dirty || saving}
                        className={`px-3 py-1 text-xs rounded inline-flex items-center gap-1 ${
                          dirty
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Save className="w-3 h-3" /> Save
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* DISCOUNTS */}
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <header className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Tag className="w-5 h-5 text-emerald-600" /> Discount codes
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Reusable percent or fixed-amount discounts you can apply to customers.</p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded inline-flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> New discount
          </button>
        </header>

        {showNew && (
          <div className="px-6 py-4 bg-emerald-50 border-b border-emerald-200">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <Field label="Code">
                <input value={newDiscount.code} onChange={e => setNewDiscount({ ...newDiscount, code: e.target.value })} className={inputClass} placeholder="EARLYBIRD" />
              </Field>
              <Field label="Name" wide>
                <input value={newDiscount.name} onChange={e => setNewDiscount({ ...newDiscount, name: e.target.value })} className={inputClass} placeholder="Early-bird 10%" />
              </Field>
              <Field label="Type">
                <select value={newDiscount.discountType} onChange={e => setNewDiscount({ ...newDiscount, discountType: e.target.value })} className={inputClass}>
                  <option value="percent">Percent</option>
                  <option value="fixed">Fixed (£)</option>
                </select>
              </Field>
              <Field label="Amount">
                <input value={newDiscount.amount} onChange={e => setNewDiscount({ ...newDiscount, amount: e.target.value })} className={inputClass} placeholder={newDiscount.discountType === 'percent' ? '10' : '50'} />
              </Field>
              <Field label="Applies to">
                <select value={newDiscount.appliesTo} onChange={e => setNewDiscount({ ...newDiscount, appliesTo: e.target.value })} className={inputClass}>
                  {APPLIES_TO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Expires">
                <input type="date" value={newDiscount.expiresAt} onChange={e => setNewDiscount({ ...newDiscount, expiresAt: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Notes" wide className="md:col-span-5">
                <input value={newDiscount.notes} onChange={e => setNewDiscount({ ...newDiscount, notes: e.target.value })} className={inputClass} placeholder="Optional internal note" />
              </Field>
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setShowNew(false); setNewDiscount({ ...EMPTY_DISCOUNT }); }} className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white">Cancel</button>
                <button onClick={createDiscount} disabled={saving} className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-60">Create</button>
              </div>
            </div>
          </div>
        )}

        <div>
          <table className="w-full text-sm divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applies to</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {discounts.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No discounts configured.</td></tr>
              )}
              {discounts.map(d => (
                <tr key={d.discountId} className={`${!d.isActive ? 'opacity-60' : ''} ${d._dirty ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-4 py-2"><input value={d.code} onChange={e => updateDiscountField(d.discountId, { code: e.target.value })} className={inputClass} /></td>
                  <td className="px-4 py-2"><input value={d.name} onChange={e => updateDiscountField(d.discountId, { name: e.target.value })} className={inputClass} /></td>
                  <td className="px-4 py-2">
                    <select value={d.discountType} onChange={e => updateDiscountField(d.discountId, { discountType: e.target.value })} className={inputClass}>
                      <option value="percent">Percent</option>
                      <option value="fixed">Fixed (£)</option>
                    </select>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <input value={d.amount} onChange={e => updateDiscountField(d.discountId, { amount: e.target.value })} className={`${inputClass} text-right`} />
                  </td>
                  <td className="px-4 py-2">
                    <select value={d.appliesTo} onChange={e => updateDiscountField(d.discountId, { appliesTo: e.target.value })} className={inputClass}>
                      {APPLIES_TO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="date"
                      value={d.expiresAt ? String(d.expiresAt).slice(0, 10) : ''}
                      onChange={e => updateDiscountField(d.discountId, { expiresAt: e.target.value || null })}
                      className={inputClass}
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!d.isActive}
                      onChange={e => updateDiscountField(d.discountId, { isActive: e.target.checked })}
                      className="w-4 h-4 accent-emerald-600"
                    />
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => saveDiscount(d)}
                      disabled={!d._dirty || saving}
                      className={`px-2 py-1 text-xs rounded inline-flex items-center gap-1 mr-1 ${
                        d._dirty ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button
                      onClick={() => deleteDiscount(d)}
                      className="p-1.5 text-red-600 hover:text-white hover:bg-red-600 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-gray-500">
        💡 Discounts are stored centrally but not yet auto-applied to invoices. Use them in commercial conversations
        and (next step) wire them into the customer's Modules tab so you can pick a discount per company.
      </p>
    </div>
  );
}

const inputClass = 'w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function PriceInput({ value, onChange }) {
  return (
    <div className="relative w-32 ml-auto">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">£</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="—"
        className="w-full pl-6 pr-2 py-1.5 text-sm text-right border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}
